import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion } from "framer-motion";

/** Up to two initials from a name ("Ahmed Ali" → "AA"). */
export const initialsOf = (name = "") =>
  String(name)
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0].toUpperCase())
    .join("") || "?";

const SIZES = {
  xs: "w-6 h-6 text-[9.5px]",
  sm: "w-8 h-8 text-[11px]",
  md: "w-10 h-10 text-[13px]",
  lg: "w-12 h-12 text-[15px]",
  xl: "w-16 h-16 text-xl",
  "2xl": "w-24 h-24 text-3xl",
};

const PREVIEW_PX = 280;

/**
 * The one employee avatar used across the app: the person's photo when there
 * is one, otherwise their initials on the orange gradient.
 * `shape`: "circle" (default) or "rounded" (rounded square, used in cards).
 * `preview`: clicking a real photo slides a larger preview out from the avatar
 * (default on; pass `preview={false}` where the avatar sits inside its own click target).
 */
const Avatar = ({ src, alt, name, size = "md", shape = "circle", className = "", title, preview = true }) => {
  const [imgError, setImgError] = useState(false);
  const [rect, setRect] = useState(null);
  const rootRef = useRef(null);
  useEffect(() => setImgError(false), [src]);
  useEffect(() => setRect(null), [src]);

  useEffect(() => {
    if (!rect) return undefined;
    const close = () => setRect(null);
    const onKey = (e) => e.key === "Escape" && close();
    window.addEventListener("keydown", onKey);
    window.addEventListener("resize", close);
    window.addEventListener("scroll", close, true);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("resize", close);
      window.removeEventListener("scroll", close, true);
    };
  }, [rect]);

  const displayName = alt || name || "";
  // Legacy avatars can be an emoji / single character.
  const isEmoji = src && src.length <= 2;
  const showImage = src && !isEmoji && !imgError;
  const canPreview = preview && showImage;
  const radius = shape === "rounded" ? "rounded-[30%]" : "rounded-full";

  // Slide out to the right of the avatar; flip left / clamp vertically if no room.
  let pos = null;
  if (rect) {
    const gap = 10;
    const fitsRight = rect.right + gap + PREVIEW_PX <= window.innerWidth - 8;
    pos = {
      left: fitsRight ? rect.right + gap : Math.max(8, rect.left - gap - PREVIEW_PX),
      top: Math.min(Math.max(8, rect.top), window.innerHeight - PREVIEW_PX - 8),
      fromX: fitsRight ? -24 : 24,
      originX: fitsRight ? "left" : "right",
    };
  }

  const openPreview = (e) => {
    e.stopPropagation();
    setRect(rootRef.current.getBoundingClientRect());
  };

  return (
    <>
    <div
      ref={rootRef}
      title={title}
      onClick={canPreview ? openPreview : undefined}
      className={`${radius} flex items-center justify-center bg-grad text-on-accent font-semibold leading-none flex-shrink-0 overflow-hidden select-none ${canPreview ? "cursor-zoom-in" : ""} ${SIZES[size] || SIZES.md} ${className}`}
    >
      {isEmoji ? (
        <span>{src}</span>
      ) : showImage ? (
        <img src={src} alt={displayName} loading="lazy" className="w-full h-full object-cover" onError={() => setImgError(true)} />
      ) : (
        <span>{initialsOf(displayName)}</span>
      )}
    </div>
    {rect &&
      createPortal(
        <AnimatePresence>
          <div key="backdrop" className="fixed inset-0 z-[9998]" onClick={(e) => { e.stopPropagation(); setRect(null); }} />
          <motion.img
            key="preview"
            src={src}
            alt={displayName}
            initial={{ opacity: 0, x: pos.fromX, scale: 0.6 }}
            animate={{ opacity: 1, x: 0, scale: 1 }}
            transition={{ duration: 0.22, ease: [0.2, 0.7, 0.2, 1] }}
            style={{ left: pos.left, top: pos.top, width: PREVIEW_PX, height: PREVIEW_PX, transformOrigin: `${pos.originX} top` }}
            className="fixed z-[9999] rounded-2xl object-cover object-top shadow-2xl border border-line2 bg-panel2"
          />
        </AnimatePresence>,
        document.body,
      )}
    </>
  );
};

export default Avatar;
